import React from 'react';
import {
  Box,
  SimpleGrid,
  Heading,
  Text,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { FiLock, FiZap, FiGift } from 'react-icons/fi';

type Accent = 'terra' | 'forest';

interface Feature {
  icon: IconType;
  title: string;
  description: string;
  accent: Accent;
}

const features: Feature[] = [
  {
    icon: FiZap,
    title: 'Simple & Fast',
    description: 'Drop a file, get a KML in milliseconds.',
    accent: 'terra',
  },
  {
    icon: FiLock,
    title: 'Private & Secure',
    description: 'Runs in your browser. Nothing is ever uploaded.',
    accent: 'forest',
  },
  {
    icon: FiGift,
    title: 'Free to Use',
    description: 'No sign-up, no limits, no catch.',
    accent: 'terra',
  },
];

const TERRA_RGB = 'var(--chakra-colors-brand-primary-rgb)';
const FOREST_RGB = 'var(--chakra-colors-brand-secondary-rgb)';

const FeatureCards = () => {
  const cardShadow = useColorModeValue(
    '0 8px 24px -12px rgba(0, 0, 0, 0.10)',
    '0 8px 24px -12px rgba(0, 0, 0, 0.45)',
  );

  return (
    <Box mt={4}>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="14px" role="list">
        {features.map((feature, idx) => {
          const isForest = feature.accent === 'forest';
          const accentToken = isForest ? 'brand.secondary' : 'brand.primary';
          const accentSoft = isForest
            ? `rgba(${FOREST_RGB}, 0.14)`
            : `rgba(${TERRA_RGB}, 0.12)`;
          return (
            <Box
              key={idx}
              bg="bg.surface"
              border="1px solid"
              borderColor="border.subtle"
              borderRadius="12px"
              padding="18px"
              position="relative"
              overflow="hidden"
              transition="all 0.25s ease"
              role="listitem"
              sx={{
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '32px',
                  height: '2px',
                  background: `var(--chakra-colors-${accentToken.replace('.', '-')})`,
                  borderRadius: '0 0 2px 0',
                  transition: 'width 0.3s ease',
                },
                '&:hover': {
                  transform: 'translateY(-3px)',
                  borderColor: `var(--chakra-colors-${accentToken.replace('.', '-')})`,
                  boxShadow: cardShadow,
                },
                '&:hover::before': {
                  width: '100%',
                },
              }}
            >
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="32px"
                h="32px"
                borderRadius="8px"
                bg={accentSoft}
                mb="10px"
              >
                <Icon as={feature.icon} w="16px" h="16px" color={accentToken} />
              </Box>
              <Heading
                as="h4"
                fontSize="15px"
                fontWeight="700"
                letterSpacing="-0.01em"
                mb="4px"
                color="text.primary"
              >
                {feature.title}
              </Heading>
              <Text fontSize="13.5px" color="text.muted" lineHeight="1.55">
                {feature.description}
              </Text>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export default FeatureCards;
